import { useEffect, useMemo, useState } from 'react'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { DrillValue } from '../components/Drill'
import {
  listInvites,
  getInvite,
  remindInvite,
  type RemoteInvite,
  type RemoteInviteDetail,
  type RemoteEmailSend
} from '../lib/adminClient'

// Invites: every share recipient with their acceptance status and whether the
// invite email went out. There are no opens or clicks: share emails carry plain
// links and no tracking pixel, so whether someone read one is not recorded.
// The list reads real data only; empty and error states are shown honestly.

function fmt(ts: number | null): string {
  if (!ts) return '—'
  const d = new Date(ts)
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
}

function ago(ts: number | null, now: number): string {
  if (!ts) return ''
  const s = Math.max(0, Math.round((now - ts) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

function StatusBadge({ status }: { status: 'invited' | 'accepted' }): JSX.Element {
  return status === 'accepted' ? (
    <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-500/15 text-emerald-300">Accepted</span>
  ) : (
    <span className="px-2 py-0.5 rounded-full text-[11px] bg-amber-500/15 text-amber-300">Pending</span>
  )
}

function DeliveryCell({ invite }: { invite: RemoteInvite }): JSX.Element {
  if (invite.sendCount === 0) {
    return <span className="text-slate-500 text-xs">no email sent</span>
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
      <span
        className={`px-1.5 py-0.5 rounded ${invite.delivered ? 'bg-white/5 text-slate-300' : 'bg-red-500/10 text-red-300'}`}
        title={invite.delivered ? 'A provider accepted the email' : 'Not delivered by a provider (logged only)'}
      >
        {invite.delivered ? 'sent' : 'not sent'}
      </span>
      {invite.sendCount > 1 && (
        <span className="px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{invite.sendCount} emails</span>
      )}
    </div>
  )
}

function SendBlock({ send }: { send: RemoteEmailSend }): JSX.Element {
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-slate-200">
          {send.kind === 'reminder' ? 'Reminder' : 'Invite'} · {fmt(send.createdAt)}
        </div>
        <div className="text-[11px] text-slate-400">
          {send.delivered ? `via ${send.provider}` : send.error ? `failed: ${send.error}` : `logged (${send.provider})`}
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mt-1">
        {send.subject || '(no subject recorded)'} · to {send.toEmail}
        {send.providerMessageId && <span className="font-mono"> · {send.providerMessageId}</span>}
      </div>
    </div>
  )
}

const INVITE_COLUMNS: Column<RemoteInvite>[] = [
  { key: 'recipient', label: 'Recipient', value: (i) => i.email, filter: 'text' },
  { key: 'owner', label: 'Invited by', value: (i) => i.ownerHandle || i.ownerEmail || '', filter: 'text' },
  { key: 'shared', label: 'Shared', value: (i) => `${i.shareKind ?? 'item'}${i.shareRevoked ? ' (revoked)' : ''}` },
  { key: 'status', label: 'Status', value: (i) => (i.status === 'accepted' ? 'Accepted' : 'Pending') },
  { key: 'invited', label: 'Invited', value: (i) => i.invitedAt ?? 0, numeric: true },
  // No opens or clicks: share emails carry no tracking pixel (see the top of this file).
  { key: 'email', label: 'Email', value: (i) => (i.sendCount === 0 ? 'no email sent' : i.delivered ? 'sent' : 'not sent') },
  { key: 'action', label: 'Action', filter: 'none', sortable: false, className: 'text-right' }
]

export default function Invites(): JSX.Element {
  const [invites, setInvites] = useState<RemoteInvite[] | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [pendingOnly, setPendingOnly] = useState(true)
  // The open row is the selected invite; its email history loads when it opens.
  const { openId: selected, toggle } = useOpenRow()
  const [detail, setDetail] = useState<RemoteInviteDetail | null>(null)
  const [reminding, setReminding] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const now = Date.now()

  async function load(): Promise<void> {
    const res = await listInvites(false)
    if (res.ok) {
      setInvites(res.data.invites)
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    if (!selected) {
      setDetail(null)
      return
    }
    let cancelled = false
    void getInvite(selected).then((r) => {
      if (cancelled) return
      if (r.ok) setDetail(r.data.invite)
    })
    return () => {
      cancelled = true
    }
  }, [selected])

  const visible = useMemo(() => {
    const all = invites ?? []
    return pendingOnly ? all.filter((i) => i.status === 'invited') : all
  }, [invites, pendingOnly])

  const pendingCount = (invites ?? []).filter((i) => i.status === 'invited').length
  const t = useTableControls(visible, INVITE_COLUMNS, 'invites')

  async function onRemind(recipientId: string): Promise<void> {
    setReminding(recipientId)
    setToast(null)
    const res = await remindInvite(recipientId)
    setReminding(null)
    if (res.ok) {
      setToast(res.data.delivered ? 'Reminder sent.' : 'Reminder logged (no email provider accepted it).')
      setDetail(res.data.invite)
      void load()
    } else {
      setToast(`Could not send reminder: ${res.error}`)
    }
  }

  const header = (
    <div className="mb-6 flex items-end justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">People</div>
        <h1 className="text-2xl font-semibold mt-1">Invites</h1>
        <div className="text-sm text-slate-400 mt-1">
          Everyone invited to a shared folder or task, whether they accepted, and how they engaged with the email.
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setPendingOnly(true)}
          className={`px-3 py-1.5 rounded-md text-sm border ${pendingOnly ? 'border-accent/60 bg-accent/15 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'}`}
        >
          Pending{pendingCount ? ` (${pendingCount})` : ''}
        </button>
        <button
          onClick={() => setPendingOnly(false)}
          className={`px-3 py-1.5 rounded-md text-sm border ${!pendingOnly ? 'border-accent/60 bg-accent/15 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'}`}
        >
          All
        </button>
      </div>
    </div>
  )

  if (state !== 'ready') {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {state === 'loading' ? 'Loading invites…' : `Could not load invites: ${error}`}
        </div>
      </div>
    )
  }

  return (
    <div>
      {header}

      {toast && (
        <div className="mb-4 glass-card rounded-lg px-4 py-2 text-sm text-slate-200">{toast}</div>
      )}

      {visible.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {pendingOnly ? 'No pending invites. Everyone invited has accepted.' : 'No invites yet.'}
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table">
          <TableHead t={t} />
            <tbody>
              {t.rows.map((i) => (
                <ExpandableRow
                  key={i.recipientId}
                  open={selected === i.recipientId}
                  onToggle={() => toggle(i.recipientId)}
                  colSpan={INVITE_COLUMNS.length}
                  testId={`invite-row-${i.recipientId}`}
                  detail={() => <InviteDetail invite={i} detail={detail?.recipientId === i.recipientId ? detail : null} />}
                >
                  <td>
                    <div className="font-medium text-slate-100">{i.email}</div>
                    {i.lastSentAt && (
                      <div className="text-[11px] text-slate-500">last emailed {ago(i.lastSentAt, now)}</div>
                    )}
                  </td>
                  <td className="text-slate-300">{i.ownerHandle || i.ownerEmail || '—'}</td>
                  <td>
                    <span className="text-slate-300">{i.shareKind ?? 'item'}</span>
                    {i.shareRevoked && <span className="ml-1 text-[11px] text-red-300">(revoked)</span>}
                  </td>
                  <td><StatusBadge status={i.status} /></td>
                  <td className="text-slate-400 text-xs">{fmt(i.invitedAt)}</td>
                  <td><DeliveryCell invite={i} /></td>
                  <td className="text-right">
                    {i.status === 'invited' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          void onRemind(i.recipientId)
                        }}
                        disabled={reminding === i.recipientId}
                        className="px-2.5 py-1 rounded-md text-xs border border-accent/40 text-accent hover:bg-accent/10 disabled:opacity-50"
                      >
                        {reminding === i.recipientId ? 'Sending…' : 'Remind'}
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-500">accepted {ago(i.acceptedAt, now)}</span>
                    )}
                  </td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  )
}

// Everything known about one invite, and every email sent for it (fetched when
// the row opens; null until it arrives).
function InviteDetail({ invite: i, detail }: { invite: RemoteInvite; detail: RemoteInviteDetail | null }): JSX.Element {
  return (
    <DetailGrid
      facts={[
        ['Recipient', i.email],
        ['Their handle', i.handle ? `@${i.handle}` : 'No account yet'],
        ['Status', i.status === 'accepted' ? 'Accepted' : 'Pending: invited, not accepted yet'],
        ['Invited', fmt(i.invitedAt)],
        ['Accepted', i.acceptedAt ? fmt(i.acceptedAt) : null],
        ['Invited by', [i.ownerHandle ? `@${i.ownerHandle}` : null, i.ownerEmail].filter(Boolean).join(' · ') || null],
        ['Shared', `${i.shareKind ?? 'item'}${i.shareRevoked ? ' (revoked: the link no longer works)' : ''}`],
        ['Share token', <span className="font-mono">{i.shareToken}</span>],
        ['Emails sent', <DrillValue to={{ set: 'emails', filters: { recipient: i.recipientId } }} label={`Emails sent for the invitation to ${i.email}`} expect={{ key: 'count', value: i.sendCount }} testId="invite-emails-records">{String(i.sendCount)}</DrillValue>],
        ['Last emailed', i.lastSentAt ? fmt(i.lastSentAt) : null],
        ['Latest email', i.sendCount === 0 ? 'No email sent' : i.delivered ? 'Accepted by the email provider' : 'Not sent: only logged, no provider accepted it'],
        ['Recipient id', <DrillValue to={{ set: 'shareRecipients', filters: { id: i.recipientId } }} label={`Invitation to ${i.email}`}><span className="font-mono">{i.recipientId}</span></DrillValue>]
      ]}
    >
      <div className="w-full grid gap-2" data-testid="invite-sends">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Email history</div>
        {!detail ? (
          <div className="text-slate-500">Loading…</div>
        ) : detail.sends.length === 0 ? (
          <div className="text-slate-500">
            No recorded email for this recipient. They were invited before emails were recorded, or another way.
          </div>
        ) : (
          detail.sends.map((s) => <SendBlock key={s.id} send={s} />)
        )}
      </div>
    </DetailGrid>
  )
}
